import React from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlayerData } from './PlayerDataContext';

function fmtDeadline(d) {
  if (!d) return '';
  return new Date(d).toLocaleString('nl-NL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export default function PlayerForms({ isMobile }) {
  const navigate = useNavigate();
  const { todayWellness, todayRpePitch, todayRpeGym, rpeForms, pendingForms, lateMatchRpe } = usePlayerData();

  const tiles = [
    {
      title:    'Wellness Check-in',
      subtitle: 'Sleep, energy, soreness & stress',
      path:     '/player/forms/wellness',
      icon:     '🌡️',
      rgbaOn:   '139,92,246',
      submitted: !!todayWellness,
    },
    {
      title:    'Pitch RPE',
      subtitle: 'Rate your pitch session effort',
      path:     '/player/forms/rpe',
      icon:     '⚽',
      rgbaOn:   '245,158,11',
      submitted: !!todayRpePitch,
    },
    // Only when a gym/individual session is on today's schedule — see rpeForms in
    // PlayerDataContext.
    ...(rpeForms?.gym ? [{
      title:    'Gym RPE',
      subtitle: 'Rate your gym session effort',
      path:     '/player/forms/rpe-gym',
      icon:     '🏋️',
      rgbaOn:   '168,85,247',
      submitted: !!todayRpeGym,
    }] : []),
    ...pendingForms.map(f => ({
      title:    f.title,
      subtitle: f.overdue ? `Overdue — was due ${fmtDeadline(f.deadline)}` : (f.deadline ? `Due ${fmtDeadline(f.deadline)}` : (f.description || 'New questionnaire')),
      path:     `/player/forms/${f.form_id}`,
      icon:     f.overdue ? '⚠️' : '📋',
      rgbaOn:   f.overdue ? '239,68,68' : '59,130,246',
      submitted: false,
    })),
    // Not a daily one-shot submission like the tiles above — it's an ongoing log — so it
    // carries no Done/Pending status (noStatus).
    {
      title:    'Food Tracker',
      subtitle: 'Log what you eat, per meal and day',
      path:     '/player/forms/nutrition',
      icon:     '🍎',
      rgbaOn:   '34,197,94',
      noStatus: true,
    },
  ];

  return (
    <div style={{ maxWidth: 700, margin: '0 auto', padding: 16 }}>
      <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 20, color: 'var(--text-primary)' }}>
        Daily Monitoring
      </h2>

      {/* Wraps on desktop (2026-08-17): the RPE split took the built-in tiles from 2 to 3, and a
          single non-wrapping row squeezed them below a readable width once a pending custom form
          was added on top. */}
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', flexWrap: 'wrap', gap: 14 }}>
        {tiles.map(tile => (
          <div key={tile.title} style={{ flex: '1 1 190px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div
            onClick={() => navigate(tile.path)}
            onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.02)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
            style={{
              height: isMobile ? 140 : '30vh', minHeight: 130,
              borderRadius: 14, position: 'relative',
              border: (tile.submitted && !tile.noStatus)
                ? '1px solid rgba(16,185,129,0.4)'
                : `1px solid rgba(${tile.rgbaOn},0.3)`,
              background: (tile.submitted && !tile.noStatus)
                ? 'rgba(16,185,129,0.07)'
                : `rgba(${tile.rgbaOn},0.08)`,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', transition: 'transform 0.2s ease', userSelect: 'none', gap: 8,
            }}
          >
            {/* Submitted badge top-right (not shown for ongoing logs like Food Tracker) */}
            {!tile.noStatus && (
              <div style={{
                position: 'absolute', top: 10, right: 12,
                fontSize: 11, fontWeight: 700,
                color: tile.submitted ? '#10b981' : '#f59e0b',
              }}>
                {tile.submitted ? '✓ Done' : '● Pending'}
              </div>
            )}

            <div style={{ fontSize: isMobile ? 34 : 40 }}>{tile.icon}</div>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', margin: 0, textAlign: 'center' }}>
              {tile.title}
            </h3>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0, textAlign: 'center', padding: '0 16px' }}>
              {tile.subtitle}
            </p>
          </div>
          {/* Late-game RPE (see lateMatchRpe in PlayerDataContext) — files against the match date. */}
          {tile.path === '/player/forms/rpe' && lateMatchRpe && (
            <button onClick={() => navigate(`/player/forms/rpe?date=${lateMatchRpe.date}`)} style={{
              padding: '10px 14px', borderRadius: 12, cursor: 'pointer', fontFamily: 'inherit',
              background: 'transparent', border: '1px dashed rgba(245,158,11,0.7)', color: '#d97706',
              fontSize: 13, fontWeight: 700,
            }}>
              Fill in game RPE{lateMatchRpe.opponent ? ` · vs ${lateMatchRpe.opponent}` : ''}
            </button>
          )}
          </div>
        ))}
      </div>
    </div>
  );
}
